import type React from 'react';
import { Fragment, PureComponent, type ReactNode } from 'react';

import {
  cloneDeep,
  differenceWith,
  isEqual,
  isObject,
  isString,
  trim,
} from 'lodash-es';

import type { AriaProps } from '@iconik-io/shared-lib/src/react/a11y/aria';

import Button from '../../../ui/button/button';
import TextFieldWrapper from '../../../ui/forms/field_wrappers/text/text';
import TextAreaFieldWrapper from '../../../ui/forms/field_wrappers/textarea/textarea';
import type { FormFieldEvent } from '../../../ui/forms/types';
import IconButton from '../../../ui/icon_button/icon_button';
import { ui } from '../../../utils/helpers';
import { urlAddProtocol } from '../../../utils/url_add_protocol';
import CopyButton from '../../copy_button/copy_button';

import styles from './url.module.css';

type ValueLabel = { value?: string; label?: string };

export type ValueType = ValueLabel[];

const removeEmptyUrlValue = (urls: ValueType) =>
  urls.filter((item) => trim(item.value) || trim(item.label));

const formatValue = (value: ValueType | undefined) => {
  let urls: ValueType = [];
  if (isString(value)) {
    urls.push({ value: value ?? '' });
  }

  if (isObject(value)) {
    urls = value.length > 0 ? cloneDeep(value) : [{ label: '', value: '' }];
  }
  return urls;
};

export type UrlProps = {
  value?: ValueType;
  readOnly?: boolean;
  label?: string;
  required?: boolean;
  name?: string;
  linkLabel?: string;
  aria?: AriaProps;
  onChange?: (value: ValueType, event?: FormFieldEvent) => void;
  onBlur?: (value: ValueType, event: FormFieldEvent) => void;
};

type State = { urls: ValueLabel[]; newUrl?: ValueLabel };

class Url extends PureComponent<UrlProps, State> {
  static getDerivedStateFromProps(
    nextProps: UrlProps,
    prevState: State,
  ): Partial<State> {
    const urls = formatValue(nextProps.value);
    const state: Partial<State> = {};
    const newValueLabelContent = differenceWith(prevState.urls, urls, isEqual);
    if (newValueLabelContent.length) {
      state.urls = urls;
    }
    return state;
  }

  state: State = {
    urls: formatValue(this.props.value),
  };

  clickButton = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  clickLink = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.stopPropagation();
  };

  changeLabel = (label: string | undefined, index: number): void => {
    const urls = cloneDeep(this.state.urls);
    urls[index] = { label, value: urls[index].value };
    this.setState({ urls });
    this.props.onChange?.(urls);
  };

  changeValue = (value: string | undefined, index: number): void => {
    const urls = cloneDeep(this.state.urls);
    urls[index] = { label: urls[index].label, value };
    this.setState({ urls });
    this.props.onChange?.(urls);
  };

  addNewValueLabel = () => {
    const urls = removeEmptyUrlValue(this.state.urls).concat([
      { label: '', value: '' },
    ]);
    this.setState({ urls });
    this.props.onChange?.(urls);
  };

  removeValueLabel = (index: number) => {
    const urls = this.state.urls.filter((_item, i) => i !== index);
    this.setState({ urls });
    this.props.onChange?.(urls);
  };

  render(): ReactNode {
    const {
      readOnly,
      label,
      required,
      name,
      linkLabel: customLinkLabel,
      aria,
    } = this.props;

    // The field renders a label and a url input per entry, so each one needs
    // its own name rather than both answering to the field's.
    const ariaFor = (part: string) =>
      aria?.label ? { ...aria, label: `${aria.label} ${part}` } : aria;

    const urls = this.state.urls;

    const lastUrl = urls[urls.length - 1];
    const showAddUrlButton = !(
      urls.length > 0 &&
      !trim(lastUrl.value) &&
      !trim(lastUrl.label)
    );

    return (
      <>
        {urls.map((valueLabel, index) => {
          const url = valueLabel.value;
          const link = trim(valueLabel.label);
          const linkLabel = link || customLinkLabel || url || 'Link';

          const buttonAfter = (
            <div className={styles.side_button_wrapper}>
              <CopyButton
                className={styles.copy_button}
                onClick={this.clickButton}
                copy={valueLabel.value ?? ''}
              />
              {!readOnly && urls.length > 1 && (
                <IconButton
                  className={styles.remove_icon}
                  variant="danger"
                  iconName="close"
                  onClick={() => this.removeValueLabel(index)}
                />
              )}
            </div>
          );

          if (!url && readOnly) {
            if (link) {
              return (
                <div key={index} className={styles.readonly}>
                  <span className={styles.link}>{link}</span>
                </div>
              );
            }
            return (
              <TextFieldWrapper
                key={index}
                name={`${name}_${index}.value`}
                aria={ariaFor('url')}
                onChange={(value) => this.changeValue(value, index)}
                disabled
              />
            );
          }

          if (readOnly) {
            const href = url ? urlAddProtocol(url) : '';
            return (
              <div key={index} className={styles.readonly}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={this.clickLink}
                  className={styles.link}
                >
                  {linkLabel}
                </a>
                {buttonAfter}
              </div>
            );
          }

          return (
            <Fragment key={index}>
              <input
                type="hidden"
                {...ui({ 'input-name': `${name}_${index}` })}
              />
              <TextFieldWrapper
                className={index > 0 ? styles.url_form_margin : undefined}
                label={label}
                required={required}
                value={link}
                name={`${name}_${index}.label`}
                aria={ariaFor('label')}
                placeholder="label"
                buttonAfter={readOnly && url ? buttonAfter : undefined}
                onChange={(value) => this.changeLabel(value, index)}
              />
              <TextAreaFieldWrapper
                value={url}
                name={`${name}_${index}.value`}
                aria={ariaFor('url')}
                onChange={(value) => this.changeValue(value, index)}
                placeholder="url"
                buttonAfter={buttonAfter}
              />
            </Fragment>
          );
        })}
        {!readOnly && showAddUrlButton && (
          <div className={styles.add_button_wrapper}>
            <Button
              iconName="plus"
              size="s"
              onClick={this.addNewValueLabel}
              noBorder
            >
              Add more URL
            </Button>
          </div>
        )}
      </>
    );
  }
}

export default Url;
